<script setup>
import { ref, computed } from 'vue';
import { useI18n } from 'vue-i18n';
import { useUISettings } from 'dashboard/composables/useUISettings';
import { formatNumber } from '@chatwoot/utils';
import wootConstants from 'dashboard/constants/globals';
import { vOnClickOutside } from '@vueuse/components';

// v6.4.25: ConversationBasicFilter comentado — status agora é dropdown direto no header
// import ConversationBasicFilter from './widgets/conversation/ConversationBasicFilter.vue';
import SwitchLayout from 'dashboard/routes/dashboard/conversation/search/SwitchLayout.vue';
import NextButton from 'dashboard/components-next/button/Button.vue';
import PlatformTooltipIcon from 'dashboard/components-next/PlatformTooltipIcon.vue';

const props = defineProps({
  pageTitle: { type: String, required: true },
  hasAppliedFilters: { type: Boolean, required: true },
  hasActiveFolders: { type: Boolean, required: true },
  activeStatus: { type: String, required: true },
  isOnExpandedLayout: { type: Boolean, required: true },
  conversationStats: { type: Object, required: true },
  isListLoading: { type: Boolean, required: true },
});

const emit = defineEmits([
  'addFolders',
  'deleteFolders',
  'resetFilters',
  'basicFilterChange',
  'filtersModal',
]);

const { t } = useI18n();
const { uiSettings, updateUISettings } = useUISettings();

// v6.4.25: Lógica do dropdown de status direto no header
const showStatusDropdown = ref(false);

const statusOptions = computed(() => [
  { label: t('CHAT_LIST.CHAT_STATUS_FILTER_ITEMS.open.TEXT'), value: 'open' },
  { label: t('CHAT_LIST.CHAT_STATUS_FILTER_ITEMS.resolved.TEXT'), value: 'resolved' },
  { label: t('CHAT_LIST.CHAT_STATUS_FILTER_ITEMS.pending.TEXT'), value: 'pending' },
  { label: t('CHAT_LIST.CHAT_STATUS_FILTER_ITEMS.snoozed.TEXT'), value: 'snoozed' },
  { label: t('CHAT_LIST.CHAT_STATUS_FILTER_ITEMS.all.TEXT'), value: 'all' },
]);

const toggleStatusDropdown = () => {
  showStatusDropdown.value = !showStatusDropdown.value;
};

const closeStatusDropdown = () => {
  showStatusDropdown.value = false;
};

const selectStatus = (value) => {
  emit('basicFilterChange', value, 'status');
  closeStatusDropdown();
};

// v6.4.25: Escape key fecha o dropdown (acessibilidade)
const onKeydown = (event) => {
  if (event.key === 'Escape' && showStatusDropdown.value) {
    closeStatusDropdown();
  }
};

const hasAppliedFiltersOrActiveFolders = computed(() => {
  return props.hasAppliedFilters || props.hasActiveFolders;
});

const allCount = computed(() => props.conversationStats?.allCount || 0);
const formattedAllCount = computed(() => formatNumber(allCount.value));

const toggleConversationLayout = () => {
  const { LAYOUT_TYPES } = wootConstants;
  const {
    conversation_display_type: conversationDisplayType = LAYOUT_TYPES.CONDENSED,
  } = uiSettings.value;
  const newViewType =
    conversationDisplayType === LAYOUT_TYPES.CONDENSED
      ? LAYOUT_TYPES.EXPANDED
      : LAYOUT_TYPES.CONDENSED;
  updateUISettings({
    conversation_display_type: newViewType,
    previously_used_conversation_display_type: newViewType,
  });
};
</script>

<template>
  <div
    class="flex items-center justify-between gap-2 px-3 h-12"
    :class="{
      'border-b border-n-strong': hasAppliedFiltersOrActiveFolders,
    }"
  >
    <div class="flex items-center justify-center min-w-0">
      <h1
        class="text-base font-medium truncate text-n-slate-12"
        :title="pageTitle"
      >
        {{ pageTitle }}
      </h1>
      <PlatformTooltipIcon identifier="conversations_sidebar" size="sm" />
      <span
        v-if="
          allCount > 0 && hasAppliedFiltersOrActiveFolders && !isListLoading
        "
        class="px-2 py-1 my-0.5 mx-1 rounded-md capitalize bg-n-slate-3 text-xxs text-n-slate-12 shrink-0"
        :title="allCount"
      >
        {{ formattedAllCount }}
      </span>

      <!-- v6.4.25: Status dropdown direto no badge (antes era span estático) -->
      <div
        v-if="!hasAppliedFiltersOrActiveFolders"
        v-on-click-outside="closeStatusDropdown"
        class="relative"
        @keydown="onKeydown"
      >
        <button
          class="flex items-center gap-1 px-2 py-1 my-0.5 mx-1 rounded-md capitalize
            text-xxs shrink-0 cursor-pointer select-none transition-colors duration-150
            border-none
            bg-n-slate-3 text-n-slate-12 hover:bg-n-slate-4
            dark:bg-n-slate-3 dark:text-n-slate-12 dark:hover:bg-n-slate-4"
          @click="toggleStatusDropdown"
        >
          <span>{{ $t(`CHAT_LIST.CHAT_STATUS_FILTER_ITEMS.${activeStatus}.TEXT`) }}</span>
          <i
            class="i-lucide-chevron-down w-3 h-3 flex-shrink-0 transition-transform duration-200"
            :class="{ 'rotate-180': showStatusDropdown }"
          />
        </button>

        <!-- Dropdown -->
        <ul
          v-show="showStatusDropdown"
          class="absolute top-full mt-1 ltr:left-0 rtl:right-0 z-50 w-40
            m-0 p-1 list-none rounded-xl border border-solid shadow-lg
            border-n-weak bg-n-background
            dark:border-n-weak dark:bg-n-background"
        >
          <li
            v-for="option in statusOptions"
            :key="option.value"
            class="flex items-center justify-between gap-2 px-3 py-1.5 rounded-lg cursor-pointer
              text-xs capitalize transition-colors duration-150
              hover:bg-n-alpha-2 dark:hover:bg-n-alpha-2"
            :class="{
              'bg-n-alpha-1 font-medium': activeStatus === option.value,
              'text-n-slate-12': true,
            }"
            @click="selectStatus(option.value)"
          >
            <span>{{ option.label }}</span>
            <i
              v-if="activeStatus === option.value"
              class="i-lucide-check w-3.5 h-3.5 text-n-brand flex-shrink-0"
            />
          </li>
        </ul>
      </div>
    </div>
    <div class="flex items-center gap-1">
      <template v-if="hasAppliedFilters && !hasActiveFolders">
        <div class="relative">
          <NextButton
            v-tooltip.top-end="$t('FILTER.CUSTOM_VIEWS.ADD.SAVE_BUTTON')"
            icon="i-lucide-save"
            slate
            xs
            faded
            @click="emit('addFolders')"
          />
          <div
            id="saveFilterTeleportTarget"
            class="absolute z-50 mt-2"
            :class="{ 'ltr:right-0 rtl:left-0': isOnExpandedLayout }"
          />
        </div>
        <NextButton
          v-tooltip.top-end="$t('FILTER.CLEAR_BUTTON_LABEL')"
          icon="i-lucide-circle-x"
          ruby
          faded
          xs
          @click="emit('resetFilters')"
        />
      </template>
      <template v-if="hasActiveFolders">
        <div class="relative">
          <NextButton
            id="toggleConversationFilterButton"
            v-tooltip.top-end="$t('FILTER.CUSTOM_VIEWS.EDIT.EDIT_BUTTON')"
            icon="i-lucide-pen-line"
            slate
            xs
            faded
            @click="emit('filtersModal')"
          />
          <div
            id="conversationFilterTeleportTarget"
            class="absolute z-50 mt-2"
            :class="{ 'ltr:right-0 rtl:left-0': isOnExpandedLayout }"
          />
        </div>
        <NextButton
          id="toggleConversationFilterButton"
          v-tooltip.top-end="$t('FILTER.CUSTOM_VIEWS.DELETE.DELETE_BUTTON')"
          icon="i-lucide-trash-2"
          ruby
          xs
          faded
          @click="emit('deleteFolders')"
        />
      </template>
      <!-- v6.4.25: Botão de filtro avançado re-habilitado para coexistir com o dropdown de status -->
      <div v-if="!hasAppliedFiltersOrActiveFolders" class="relative">
        <NextButton
          id="toggleConversationFilterButton"
          v-tooltip.right="$t('FILTER.TOOLTIP_LABEL')"
          icon="i-lucide-list-filter"
          slate
          xs
          faded
          @click="emit('filtersModal')"
        />
        <div
          id="conversationFilterTeleportTarget"
          class="absolute z-50 mt-2"
          :class="{ 'ltr:right-0 rtl:left-0': isOnExpandedLayout }"
        />
      </div>
      <!-- v6.4.25: ConversationBasicFilter comentado — status controlado pelo dropdown direto
      <ConversationBasicFilter
        v-if="!hasAppliedFiltersOrActiveFolders"
        :is-on-expanded-layout="isOnExpandedLayout"
        @change-filter="onBasicFilterChange"
      />
      -->
      <SwitchLayout
        :is-on-expanded-layout="isOnExpandedLayout"
        @toggle="toggleConversationLayout"
      />
    </div>
  </div>
</template>
